import React from "react";
import { MessageSquare, Check, Loader2, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { getScoreColor } from "./scoreColor";
import type { SaveState } from "./useMatchInterviewPrep";

interface InterviewPrepHeaderProps {
	overallScore: number | null;
	answeredCount: number;
	totalQuestions: number;
	saveState: SaveState;
}

export const InterviewPrepHeader = ({
	overallScore,
	answeredCount,
	totalQuestions,
	saveState,
}: InterviewPrepHeaderProps) => {
	const { t } = useTranslation();

	// overall_compatibility_score is a percentage (0-100), translate to 0-10 for color lookup
	const scoreForColor = overallScore !== null ? overallScore / 10 : 0;
	const colors = getScoreColor(scoreForColor);

	const renderSaveIndicator = () => {
		if (saveState === "saving")
			return (
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<Loader2 className="h-3 w-3 animate-spin" />
					{t("matching.interviewPrep.saving", "Saving…")}
				</span>
			);
		if (saveState === "saved")
			return (
				<span className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
					<Check className="h-3 w-3" />
					{t("matching.interviewPrep.saved", "Saved")}
				</span>
			);
		if (saveState === "error")
			return (
				<span className="flex items-center gap-1.5 text-xs text-destructive">
					<AlertCircle className="h-3 w-3" />
					{t("matching.interviewPrep.saveError", "Couldn't save")}
				</span>
			);
		return null;
	};

	return (
		<div className="flex items-start justify-between gap-4 mb-6">
			<div className="flex items-start gap-3">
				<div className="p-2 rounded-sm bg-primary/10 text-primary">
					<MessageSquare className="h-5 w-5" />
				</div>
				<div>
					<h3 className="text-xl font-semibold text-foreground">
						{t("matching.interviewPrep.title", "Interview Preparation")}
					</h3>
					<p className="text-sm text-muted-foreground mt-0.5">
						{t(
							"matching.interviewPrep.subtitle",
							"Your AI-curated briefing to run this interview with confidence"
						)}
					</p>
				</div>
			</div>
			<div className="flex flex-col items-end gap-2 shrink-0">
				{overallScore !== null && (
					<Badge
						className={`text-sm font-semibold ${colors.bgClass} ${colors.textClass} ${colors.borderClass}`}
						variant="outline"
					>
						{t("matching.interviewPrep.compatibility", "Match")} · {Math.round(overallScore)}%
					</Badge>
				)}
				{totalQuestions > 0 && (
					<span className="text-xs text-muted-foreground">
						{answeredCount}/{totalQuestions}{" "}
						{t("matching.interviewPrep.prepared", "prepared")}
					</span>
				)}
				{renderSaveIndicator()}
			</div>
		</div>
	);
};
